// AnaMoni microcopy — single source of truth.
// See ANAMONI_MICROCOPY.md. Brand `AnaMoni` lives ONLY in user-facing strings.

export const ANAMONI = {
  brand: "AnaMoni",
  glyph: "✦",

  // §2.1 — empty state
  placeholders: [
    'Ask AnaMoni: "Spanish associates with T1D screening"',
    'Ask AnaMoni: "Recently certified contacts in Italy"',
    'Ask AnaMoni: "Country leads with Clinical Trials cert"',
  ],
  ariaLabel: "Ask AnaMoni",
  ariaDescribed:
    "Natural-language search. Type a question and press Enter, or press Cmd+K to focus from anywhere.",
  kbdHint: "⌘K",
  clearLabel: "Clear search",

  // §2.2 — thinking
  thinking: "AnaMoni is thinking…",
  thinkingSr: "Searching",
  etaSlow: "Still working — complex queries can take a few seconds.",
  etaVerySlow:
    "This is taking longer than usual. You can keep waiting or use the filter rail.",

  // §2.3 — high-confidence success
  successHint:
    "AnaMoni interpreted your question. Edit the filters below to adjust.",
  dismissHint: "Dismiss hint",

  // §2.4 — clarify banner footer
  clarifyHelper: "Press 1–4 to pick · Esc to dismiss",
  clarifyPrefix: "AnaMoni",
  clarifyUseAnyway: "Use anyway",
  clarifyRephrase: "Rephrase",
  clarifyUseFilters: "Use filters instead",

  // §2.5 — out-of-domain (Q027 sentinel, Q14 warning chrome)
  outOfDomainHeadline: "That doesn't match anything I can search yet.",
  outOfDomainBody:
    'Try the Build advanced filter panel on the left, or rephrase using a field name like "Country of Residence" or "Certification Type".',
  outOfDomainPrimaryAction: "Open advanced filter",
  outOfDomainSecondaryAction: "Rephrase",

  // §2.6 — errors
  errors: {
    bedrockOffline: {
      message:
        "AnaMoni is offline right now. Use the filter rail, or try again in a moment.",
      action: "Retry",
    },
    bedrockTimeout: {
      message:
        "AnaMoni didn't respond in time. Your question may be too broad — try narrowing it, or use the filter rail.",
      action: "Retry",
    },
    schemaParseFail: {
      message:
        "AnaMoni gave an answer I couldn't read. This is a bug — the team has been notified.",
      action: "Retry",
    },
    salesforceUnreachable: {
      message:
        "Couldn't reach Salesforce to run AnaMoni's query. Refresh the cache or try again.",
      action: "Refresh cache",
    },
    authExpired: {
      message:
        "Your session expired while AnaMoni was searching. Sign in to continue.",
      action: "Sign in",
    },
    rateLimited: {
      message: "AnaMoni is busy — please wait a few seconds and try again.",
      action: "Retry in 5s",
    },
  },

  // §2.7 — Summarise button
  summariseIdle: "Summarise",
  summariseLoading: "Summarising…",
  summariseDisabledNoResults: "Run a search first.",
  summariseDisabledTooMany: "Too many results to summarise — narrow to 50 or fewer.",
  summariseModalHeader: "AnaMoni summary",
  summariseModalSubtitle: (n: number) => `Based on ${n} results matching your filters.`,
  summariseEmpty: (n: number) =>
    `AnaMoni couldn't find a useful pattern in these ${n} results. Try filtering further or open contacts individually.`,

  // §2.9 — empty state copy
  emptyStates: {
    noResults: {
      headline: "No contacts match these filters",
      body: "Try widening one of your filters or clearing the search.",
    },
    nlClarify: {
      headline: "AnaMoni needs more detail",
      body: "Pick one of the suggestions above, edit the query, or use the filter rail.",
    },
    nlNoMatch: (terms: string) => ({
      headline: `No matches for "${terms}"`,
      body: "Try different keywords or switch to filters.",
    }),
    nlOutOfDomain: {
      headline: "That doesn't match anything I can search yet",
      body: "Try the advanced filter, or rephrase using a field name.",
    },
    noData: {
      headline: "No INPACT contacts yet",
      body:
        "Once contacts are added in Salesforce with INPACT_Associate_Status set, they'll appear here.",
    },
    error: (msg: string) => ({
      headline: "Couldn't reach Salesforce",
      body: `${msg}. The team has been notified.`,
    }),
  },
} as const;

/** Suggested saved-view name (§2.8) — pure string utility. */
export function suggestSavedViewName(opts: {
  countries?: string[];
  status?: string;
  cert?: string;
  isCountryLead?: boolean;
  isRecent?: boolean;
  isInactiveFalseOnly?: boolean;
  freeTextTerms?: string[];
}): string {
  const { countries = [], status, cert, isCountryLead, isRecent, isInactiveFalseOnly, freeTextTerms } = opts;

  if (status && countries.length === 1) return `${status} in ${countries[0]}`;
  if (cert && countries.length === 1) return `${cert} — ${countries[0]}`;
  if (status && countries.length > 1) return `${status} across ${countries.length} countries`;
  if (isRecent && cert) return `Recently certified`;
  if (isCountryLead && countries.length === 1) return `Country leads — ${countries[0]}`;
  if (isInactiveFalseOnly && status) return `Active ${status}`;
  if (freeTextTerms && freeTextTerms.length > 0) {
    return `Search: "${freeTextTerms.slice(0, 3).join(" ")}…"`;
  }
  const today = new Date().toISOString().slice(0, 10);
  return `Untitled view (${today})`;
}
